import React, { useState } from 'react';
import {
  InfoCard,
  InfoLabel,
  InfoValue,
  IconButton,
  ManualTypeBar,
  ManualTypeInfo,
  IconButtonGroup,
} from './StyledComponents';
import Modal from './Modal';
import { apiService } from '../services/api';
import { FiDownload } from 'react-icons/fi';
import { MdOutlineRemoveRedEye } from 'react-icons/md';

type ManualType = keyof typeof manualTypeMap;

// Updated interface with currentManualText prop
interface InfoDisplayProps {
  label: string;
  value: string | null;
  currentManualText?: string; // Pass the current manual text
}
// Map value to "Betreibende" -> Handbuch für Betreibende
// "UntM" -> Handbuch für das Unterkunftsmanagement
// "ZuwSt" -> Handbuch für die Zuweisende Stelle

const manualTypeMap = {
  "Betreibende": "Handbuch für Betreibende",
  "UntM": "Handbuch für das Unterkunftsmanagement",
  "ZuwSt": "Handbuch für die Zuweisende Stelle",
};
const InfoDisplay: React.FC<InfoDisplayProps> = ({ 
  label, 
  value, 
  currentManualText
}) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  if (!value || !(value in manualTypeMap)) {
    return null;
  }

    const manualType = value as ManualType;

  const handleInfoClick = async () => {
    try {
      // Use the current manual text if available, otherwise fetch from backend
      if (currentManualText) {
        setModalContent(currentManualText);
        setModalOpen(true);
      } else {
        const data = await apiService.getManualContent(manualType);
        setModalContent(data.content);
        setModalOpen(true);
      }
    } catch (error) {
      console.error('Error in handleInfoClick:', error);
      setModalContent('Failed to load manual content.');
      setModalOpen(true);
    }
  };

  const handleDownload = async () => {
    if (isDownloading) return; // Prevent multiple downloads
    
    setIsDownloading(true);
    try {
      console.log('Starting download...');
      
      // Use apiService to download the manual
      const blob = await apiService.downloadManual(manualType);
      
      console.log('Creating download link...');
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Use default filename
      let filename = `Handbuch_${manualType}.docx`;
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      console.log('Download completed successfully');
    } catch (error) {
      console.error('Error downloading manual:', error);
      alert('Failed to download manual. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <InfoCard>
      <ManualTypeBar>
        <ManualTypeInfo>
          <InfoLabel>{label}:</InfoLabel>
          <InfoValue>{manualTypeMap[manualType]}</InfoValue>
        </ManualTypeInfo>
        <IconButtonGroup>
          <IconButton 
            onClick={handleInfoClick} 
            title="View Manual"
          >
            <MdOutlineRemoveRedEye />
          </IconButton>
          <IconButton 
            onClick={handleDownload} 
            title="Download as DOCX" 
            disabled={isDownloading}
            style={{ 
              opacity: isDownloading ? 0.6 : 1
            }}
          >
            <FiDownload />
          </IconButton>
        </IconButtonGroup>
      </ManualTypeBar>
      <Modal 
        isOpen={isModalOpen} 
        onClose={() => setModalOpen(false)} 
        content={modalContent} 
        manualType={manualType}
      />
    </InfoCard>
  );
};

export default InfoDisplay;
